import * as React from 'react';
import { t } from '@reos/i18n';
import { systemClock } from '@reos/config';
import {
  caseFreshness,
  clientReportState,
  freshnessAgeText,
  type ClientReportExportStatus,
  type CockpitTask,
} from '../lib/case-cockpit.js';
import { exportDownloadUrl, listCaseExports, reportPreviewUrl, requestPdfExport } from '../lib/exports-api.js';
import { listPendingReleases, releaseReport, type PendingReleaseItem } from '../lib/reports-release-api.js';
import type { CaseDetail } from '../lib/dd-api.js';

const cs = { locale: 'cs' } as const;

/** Druh reportu, ktery se posila klientovi (krok REP1). */
const CLIENT_REPORT_KIND = 'report-klient' as const;

/**
 * „Co dám klientovi?" — nahled reportu, objednani PDF, schvaleni a stav
 * odeslani. Krok UX2 to mel jako ctvrtou kartu kokpitu; krok UXR2 to presunul
 * sem, na zalozku **Dokumenty** (ROZHODNUTI UXR2-4): predloha
 * `03-detail-zakazky.html` ma na kokpitu tri otazky a report JE dokument —
 * stoji tu vedle objednavek exportu, ktere uz zalozka mela.
 *
 * ROZHODNUTI UX2-4 (prevzato beze zmeny): fronta „Ke schválení" po uvolneni
 * objednavku ztraci, takze „odeslano kdy komu" se po obnoveni stranky precte
 * jen z odpovedi `POST …/release`.
 */
export function ClientReportCard({
  detail,
  zakazkaId,
  tasks,
}: {
  readonly detail: CaseDetail;
  /** `cases.id` — pod nim hledame objednavku reportu ve fronte uvolneni. */
  readonly zakazkaId: string;
  /** Ukoly makléře — co jeste chybi, nez report pujde klientovi. */
  readonly tasks: readonly CockpitTask[];
}): React.JSX.Element {
  const ddCaseId = detail.fixture.caseInfo.id;
  const freshness = caseFreshness(detail);

  /**
   * Krok TOKYFIX1 (prekazka P3/5): karta cte STAV VYROBY, ne jen hotovy soubor.
   * `status: 'none'` = export si nikdo nevyzadal. Dokud se karta ridila jen
   * hotovym PDF, nemela co rict o exportu ve fronte ani o tom, ktery selhal.
   */
  const [pdf, setPdf] = React.useState<{
    ready: boolean;
    href: string | null;
    status: ClientReportExportStatus;
    failureReason: string | null;
  }>({ ready: false, href: null, status: 'none', failureReason: null });
  const [pending, setPending] = React.useState<PendingReleaseItem | null>(null);
  const [released, setReleased] = React.useState<{ at: string; email: string } | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const load = React.useCallback((): void => {
    listCaseExports(ddCaseId).then(
      (items) => {
        const mine = items.filter((item) => item.kind === CLIENT_REPORT_KIND);
        // Historie prichazi od nejnovejsiho (`order by created_at desc`).
        const done = mine.find((item) => item.status === 'DONE');
        const latest = mine[0];
        setPdf({
          ready: done !== undefined,
          href: done === undefined ? null : exportDownloadUrl(done),
          status: done !== undefined ? 'DONE' : ((latest?.status ?? 'none') as ClientReportExportStatus),
          failureReason: latest?.failureReason ?? null,
        });
      },
      () => setPdf({ ready: false, href: null, status: 'none', failureReason: null }),
    );
    listPendingReleases().then(
      (items) => { setPending(items.find((item) => item.caseId === zakazkaId) ?? null); },
      () => setPending(null),
    );
  }, [ddCaseId, zakazkaId]);

  React.useEffect(load, [load]);

  /*
   * Krok TOKYFIX1 (prekazka P3/5): dokud PDF ve fronte, karta se sama
   * obnovuje — tentyz interval jako „Export reportů" (`zakazka-detail.tsx`),
   * aby makler(ka) nemusel(a) hadat, kdy obnovit stranku.
   */
  React.useEffect(() => {
    if (pdf.status !== 'QUEUED' && pdf.status !== 'RUNNING') return;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [pdf.status, load]);

  const report = clientReportState({
    pdfReady: pdf.ready,
    released,
    pendingOrderId: pending?.orderId ?? null,
    missing: tasks.filter((task) => !task.done).map((task) => task.text),
    exportStatus: pdf.status,
    exportError: pdf.failureReason,
  });

  const approveReport = (): void => {
    if (pending === null) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    void releaseReport(pending.orderId, '')
      .then((order) => {
        // Odpoved ma `deliveredAt` a `email` — po refreshi uz to API nesdela.
        setReleased({ at: order.deliveredAt ?? systemClock.now().toISOString(), email: order.email });
        setPending(null);
        setNotice(t('webAdmin.caseCockpit.report.released', cs));
      })
      .catch((cause: unknown) => {
        setError(
          t('webAdmin.caseCockpit.report.failed', {
            ...cs,
            values: { error: cause instanceof Error ? cause.message : String(cause) },
          }),
        );
      })
      .finally(() => { setBusy(false); });
  };

  const orderPdf = (): void => {
    setBusy(true);
    setError(null);
    setNotice(null);
    void requestPdfExport(ddCaseId, CLIENT_REPORT_KIND)
      .then(() => {
        setNotice(t('webAdmin.caseCockpit.report.queuedNote', cs));
        load();
      })
      // `DomainApiError.message` uz je VETA SERVERU (krok PIL3 bod 4) — vcetne
      // „podklad reportu ještě není" s dalsim krokem.
      .catch((cause: unknown) => { setError(cause instanceof Error ? cause.message : String(cause)); })
      .finally(() => { setBusy(false); });
  };

  return (
    <section className="rh-a2-card" data-testid="case-cockpit-report">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.caseCockpit.report.heading', cs)}</h2>
      </div>
      <p className="rh-a2-card__hint">{t('webAdmin.caseCockpit.report.hint', cs)}</p>
      <p style={{ margin: '0 0 10px' }} data-testid="case-cockpit-report-status">
        {report.headline}
      </p>
      {freshness.stale ? (
        <p className="rh-a2-meta" style={{ margin: '0 0 10px' }} data-testid="case-cockpit-report-stale">
          {freshness.policyChanged
            ? t('webAdmin.caseCockpit.freshness.stalePolicyHint', cs)
            : t('webAdmin.caseCockpit.freshness.staleHint', {
                ...cs,
                // Krok UXP4b bod 2 (nalez P2-6): „před 0 dny" → „dnes" (SSOT
                // `freshnessAgeText`, aby stari znelo stejne na vsech mistech).
                values: { when: freshnessAgeText(freshness.ageDays) },
              })}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <a
          className="rh-a2-btn"
          href={reportPreviewUrl(ddCaseId, CLIENT_REPORT_KIND)}
          target="_blank"
          rel="noreferrer"
          data-testid="case-cockpit-report-preview"
        >
          {t('webAdmin.caseCockpit.report.preview', cs)}
        </a>
        {pdf.href !== null ? (
          <a className="rh-a2-btn" href={pdf.href} data-testid="case-cockpit-report-download">
            {t('webAdmin.exports.download', cs)}
          </a>
        ) : null}
        {!pdf.ready ? (
          <button
            type="button"
            className="rh-a2-btn"
            disabled={busy}
            onClick={orderPdf}
            data-testid="case-cockpit-report-order"
          >
            {/* Krok UXP4b bod 2: JEDNO slovo pro vyrobu PDF na vsech mistech
                (karta reportu i „Export reportů") — do UXP4b tu stalo
                „Vyrobit PDF" a v exportech „Objednat PDF" (nalez P2-6). */}
            {t('webAdmin.exports.pdf', cs)}
          </button>
        ) : null}
        <button
          type="button"
          className="rh-a2-btn rh-a2-btn--primary"
          disabled={report.approveDisabled || busy}
          onClick={approveReport}
          data-testid="case-cockpit-report-approve"
        >
          {t('webAdmin.caseCockpit.report.approve', cs)}
        </button>
      </div>
      {/*
        Krok TOKYFIX1 (prekazka P3/5): „žádná objednávka" uz neni ODPOVED NA
        STAV REPORTU (tu rika `case-cockpit-report-status`), ale vysvetleni,
        proc je zhasnute „Schválit a poslat klientovi": uvolnuje se objednavka
        z VEREJNEHO PRODEJE reportu, kterou bezna zakazka kancelare nema.
      */}
      {pending === null && released === null && pdf.ready ? (
        <p className="rh-a2-meta" style={{ margin: '10px 0 0' }} data-testid="case-cockpit-report-no-order">
          {t('webAdmin.caseCockpit.report.noOrder', cs)}
        </p>
      ) : null}
      {error !== null ? (
        <p role="alert" className="rh-a2-error" style={{ margin: '10px 0 0' }}>
          {error}
        </p>
      ) : null}
      {notice !== null ? (
        <p role="status" className="rh-a2-meta" style={{ margin: '10px 0 0' }}>
          {notice}
        </p>
      ) : null}

      <div className="rh-a2-card__foot" data-testid="case-cockpit-report-missing">
        <p style={{ margin: '0 0 4px', fontWeight: 600, color: 'var(--rh-text)' }}>
          {report.missing.length === 0
            ? t('webAdmin.caseCockpit.report.missingEmpty', cs)
            : t('webAdmin.caseCockpit.report.missingTitle', cs)}
        </p>
        {report.missing.length > 0 ? (
          <>
            <p style={{ margin: '0 0 6px' }}>{t('webAdmin.caseCockpit.report.missingHint', cs)}</p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {report.missing.slice(0, 6).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            {report.missing.length > 6 ? (
              <p style={{ margin: '4px 0 0' }}>
                {t('webAdmin.caseCockpit.report.missingMore', {
                  ...cs,
                  values: { count: String(report.missing.length - 6) },
                })}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
